<script>
    import { onMount } from "svelte";
    import { push } from "svelte-spa-router";
    import {
        isAuthenticated,
        hostname,
        username,
        userLocation,
        environment,
        stack,
        accessInfo,
        showDebug,
        showDebugPane
    } from "../stores.js";
    import Close from "svelte-material-icons/Close.svelte";
    import Refresh from "svelte-material-icons/Refresh.svelte";
    import Chevrondown from "svelte-material-icons/ChevronDown.svelte";
    import ChevronUp from "svelte-material-icons/ChevronUp.svelte";
    import Header from "../components/Header.svelte";
    import CheckboxBlankOutline from "svelte-material-icons/CheckboxBlankOutline.svelte";
    import CheckboxIntermediate from "svelte-material-icons/CheckboxIntermediate.svelte";
    import FilterMenuOutline from "svelte-material-icons/FilterMenuOutline.svelte";
    import FilterMenu from "svelte-material-icons/FilterMenu.svelte";
    import StorageWidget from '../components/StorageWidget.svelte';

    import {
        isoDateToTime,
        isoDateToDate,
        isNumeric,
        getTeamsInfo
    } from "../utils.js";
    import { toast } from "@zerodevx/svelte-toast";
    import { toastThemeError} from "../toasts.js";
    import { pipelineObserver, allPipelines, availableSessions, sessionsPageSize, sessionsSkip, availableSessionsTotalCount, isSessionsListLegacy, isSessionsListLoading, isPipelinesListLoading, sessionFilter } from "../pipelinestores.js";
    import { tooltip } from "svooltip";
    import 'svooltip/styles.css';

    $: {
        if ($isAuthenticated && $accessInfo?.stacks?.length > 0) {
            console.log("~~~~~~~~~~~~~~~INITIALISING BECAUSE AUTHENTICATED~~~~~~~~~~~~~~~~");
            initialiseWithData();
        }
    }

    $: {
        if ($isPipelinesListLoading == false) {
            showPipelines($allPipelines);
        }
    }

    export let machine;

    let activePipelines = [];
    let allMyTeams = [];
    let allMembers = [];
    let openTeams = false;
    let openUsers = false;
    let openSort = false;
    let requestBuffer;
    let timeoutCounter;
    const sortOrder = [
        { name: "a-z", _id: "sample_name_az" },
        { name: "z-a", _id: "sample_name_za" },
        { name: "Newest", _id: "date_latest_first" },
        { name: "Oldest", _id: "date_oldest_first" },
    ];

    async function initialiseWithData() {
        console.log("INITIALISING HOME SCREEN WITH DATA FROM STACK: ", $stack);
        console.log("...AND WITH DATA FOR TEAMS: ", $accessInfo?.userservice_org_teams);

        // Get organization from stack. Exciscope is the only one with multiple stacks atm.
        await refreshTeamInfo();
        showPipelines($allPipelines);
    }

    // Manipulate the data from the server before displaying
    let hideOldPipelines = true;
    const cutoffAgeSeconds = 24 * 3600 * 3;
    async function showPipelines(pipelines) {
        try {
            // Filter away old pipelines
            if (hideOldPipelines) {
                pipelines = pipelines?.filter((p) => {
                    const date = new Date(p.start_time).getTime();
                    const diff = Date.now() - date;
                    const diffSeconds = diff / 1000;
                    const secondsBetweenDates = Math.abs(diffSeconds);
                    if (secondsBetweenDates > cutoffAgeSeconds) {
                        return false; // Discard old (2 days) pipelines
                    }
                    return true;
                });
            }

            // Sort remaining pipelines by date
            pipelines?.sort((a, b) => {
                const dateA = new Date(a.start_time).getTime();
                const dateB = new Date(b.start_time).getTime();
                return dateA > dateB ? -1 : 1; // Descending order
            });
            console.log("Home page sees pipelines object sorted = ", pipelines);
            activePipelines = [...pipelines];
        } catch (error) {
            console.log("PARSE PIPELINES ERROR:", error.message);
            activePipelines = [];
        }
    }

    async function refreshTeamInfo() {
        try {
            let stackOrganization = $accessInfo?.userservice_org_teams.find((org) =>
                org.stacks.some((s) => s.name == $stack),
            );
            console.log(stackOrganization);
            const orgId = stackOrganization?.stacks?.find((s) => s.name == $stack).organizationId;
            const response = await getTeamsInfo($hostname, orgId);
            if(isNumeric(response)){
                throw Error;
            }
            allMyTeams = response;
            let newMember = [];
            allMyTeams.forEach((team) =>
                team.members.forEach((member) =>
                    newMember.some((user) => user._id === member._id) ? null : newMember.push(member),
                )
            );
            allMembers = newMember;
        } catch (err) {
            toast.push("Failing fetching teams and users", toastThemeError);
        }
    }

    // Helper function to extract a shorter id (without path and json) for a pipeline_id
    function getPipelineNumber(pipelineId) {
        const regex = /pipelines\/(.+)$/;
        const id = pipelineId?.match(regex);
        if (!id) return;
        return id[1];
    }

    // Helper function to extract session for a pipeline_id
    function getPipelineSession(pipelineId) {
        const regex = /sessions\/(.+)\/pipelines\/.+$/;
        const id = pipelineId?.match(regex);
        if (!id) return;
        return id[1];
    }

    // Helper function to look up session name (as opposed to the id), e.g.
    // id = ses-230922-132613-ipren-400mg-fast-scan
    // -> name = "Ipren 400mg fast scan"
    function getPipelineSessionName(pipelineId) {
        const sid = getPipelineSession(pipelineId);
        if (!sid) return;
        const foundSession = $availableSessions?.find(s => s.session_id == sid);
        return foundSession?.name || sid;
    }

    // Helper function to extract interactivity mode (urgency) for a pipeline_id
    // TODO: Unfortunately it is not entirely true that
    // BATCH means Automatic, because BATCH can also mean FDK reconstruction.
    // We will disable this column until we have either come up with a better naming
    // or we have a way to determine which one it is.
    function getPipelineInteractivityTrigger(urgency) {
        if (!urgency) return "";
        switch (urgency) {
            case 'BATCH': return "Automatic";
            case 'INTERACTIVE': return "User";
            default: return 'UNKNOWN';
        }
    }

    async function onSelectSession(session) {
        console.log("SELECTED SESSION: ", session.session_id);
        if (session && session.session_id) {
            // Navigate to the details view
            push(`/details/${session.session_id}`);
        }
    }

    async function onRefreshPipelines() {
        await $pipelineObserver.refreshPipelines();
        showPipelines($allPipelines);
    }

    async function onRefreshSessions() {
        await $pipelineObserver.refreshSessions();
    }

    async function onChangedStack() {
        console.log("SELECTED STACK:", $stack);
        $sessionFilter = {userIds:[], teamIds:[], sortOrder:""};
        await onRefreshPipelines();
        await onRefreshSessions();
        await refreshTeamInfo();
    }

    function onClickMember(e, userId) {
        e.stopPropagation();
        if ($sessionFilter.userIds.includes(userId)) {
            const removedUserArray = $sessionFilter.userIds.filter((id) => id !== userId);
            $sessionFilter.userIds = removedUserArray;
            return filterNewSession();
        }
        $sessionFilter.userIds = [...$sessionFilter.userIds, userId];
        return filterNewSession();
    }

    function onClickTeam(e, teamId) {
        e.stopPropagation();
        if ($sessionFilter.teamIds.includes(teamId)) {
            const removedTeamArray = $sessionFilter.teamIds.filter((id) => id !== teamId);
            $sessionFilter.teamIds = removedTeamArray;
            return filterNewSession();
        }
        $sessionFilter.teamIds = [...$sessionFilter.teamIds, teamId];
        return filterNewSession();
    }

    function onClickOrder(orderId) {
        if($sessionFilter.sortOrder == orderId) {
            $sessionFilter.sortOrder = "";
        }
        else {
            $sessionFilter.sortOrder = orderId;
        }
        filterNewSession();
    }

    // But a buffer on the request call to the api. So it doesn't trigger a request call every time
    // a filter is changed, but rather when the user is done selecting filters
    function filterNewSession() {
        const waitTimeForRequestMs = 1000;
        if ($isSessionsListLoading === true || timeoutCounter > 7) {
            timeoutCounter += 1;
            return setTimeout(filterNewSession, waitTimeForRequestMs);
        }
        clearTimeout(requestBuffer);
        requestBuffer = setTimeout(
            () => {
                $pipelineObserver.refreshSessions();
                timeoutCounter = 0;
            }
            ,waitTimeForRequestMs
        );
    }

    function getUserFromSession(session) {
        const userId = session?.user_id;
        console.log('SESSION ', session?.session_id, ' HAS USER ID ', session?.user_id);
        if (userId) return `${userId?.substring(0,3)}..${userId?.substring(userId.length - 3)}`;
        else return undefined;
    }

    async function nextSessionPage() {
        let newSkip = $sessionsSkip + $sessionsPageSize;
        if (newSkip >= $availableSessionsTotalCount) {
            return;
        }
        console.log('NEXT SESSION PAGE REQUESTED. NEW SKIP: ', newSkip);
        $sessionsSkip = newSkip;

        await onRefreshSessions();
        console.log("REFRESHED NEXT SESSIONS: ", $availableSessions);
        await onRefreshPipelines();
        console.log("REFRESHED NEXT PIPELINES: ", activePipelines);
    }

    async function previousSessionPage() {
        let newSkip = $sessionsSkip - $sessionsPageSize;
        if (newSkip < 0) {
            return;
        }
        console.log('PREV SESSION PAGE REQUESTED. NEW SKIP: ', newSkip);
        $sessionsSkip = newSkip;

        await onRefreshSessions();
        console.log("REFRESHED PREVIOUS SESSIONS: ", $availableSessions);
        await onRefreshPipelines();
        console.log("REFRESHED PREVIOUS PIPELINES: ", activePipelines);
    }

    function closeDebugPane() {
        $showDebugPane = false;
    }

    function openUserMenu() {
        if (openUsers === true) {
            return closeUserMenu();
        }
        closeTeamMenu();
        closeSortMenu();
        openUsers = true;
        document.addEventListener("click", closeUserMenu);
    }

    function closeUserMenu() {
        openUsers = false;
        document.removeEventListener("click", closeUserMenu);
    }

    function openTeamMenu() {
        if (openTeams === true) {
            return closeTeamMenu();
        }
        closeUserMenu();
        closeSortMenu();
        openTeams = true;
        document.addEventListener("click", closeTeamMenu);
    }

    function closeTeamMenu() {
        openTeams = false;
        document.removeEventListener("click", closeTeamMenu);
    }

    function openSortMenu() {
        if (openSort === true) {
            return closeSortMenu();
        }
        closeTeamMenu();
        closeUserMenu();
        openSort = true;
        document.addEventListener("click", closeSortMenu);
    }

    function closeSortMenu() {
        openSort = false;
        document.removeEventListener("click", closeSortMenu);
    }

    onMount(() => {
        // onDestroy:
        return () => {
            document.removeEventListener("click", closeSortMenu);
            document.removeEventListener("click", closeTeamMenu);
            document.removeEventListener("click", closeUserMenu);
        };
    });
</script>

<Header on:change={onChangedStack} />

<main-window>
    <pipelines-pane>
        <h2 style="display:inline-block">Ongoing pipelines</h2>
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <refresh-button class={$isPipelinesListLoading ? "rotate" : ""} on:click={onRefreshPipelines}>
            <Refresh color="currentColor" size="1.5em" />
        </refresh-button>
        <annotation>Recent and active processing pipelines</annotation>
        {#if $showDebug}
            Active pipelines: {activePipelines?.length}
            Sessions: {$availableSessions?.length}
        {/if}
        <table style="table-layout: fixed;">
            <thead>
                <th>Start time</th>
                <th>Session</th>
                <th>ID</th>
                <th>Type</th>
                <th>Scan</th>
                <!--th>Trigger</th-->
                <th>Status</th>
                <th>Finished</th>
            </thead>
            <tbody>
                {#if activePipelines?.length > 0}
                    {#each activePipelines as p}
                        <tr>
                            <td>
                                {isoDateToDate(p.start_time)}
                                {isoDateToTime(p.start_time)}
                            </td>
                            <td>{getPipelineSession(p.pipeline_id) || ""}</td>
                            <td>{getPipelineNumber(p.pipeline_id) || ""}</td>
                            <td>{p.api}</td>
                            <td>{p.tomography_dataset_id || ""}</td>
                            <!--td>{getPipelineInteractivityTrigger(p.urgency) || ""}</td-->
                            <td>{p.status || ""}</td>
                            <td>
                                {isoDateToDate(p.finish_time)}
                                {isoDateToTime(p.finish_time)}
                            </td>
                        </tr>
                    {/each}
                {:else}
                    <tr><td colspan="100%">No active pipelines</td></tr>
                {/if}
            </tbody>
            <tfoot style="text-align: center; border: none;">
                <tr>
                    <td colspan="100%" style="border: none;">
                        {#if hideOldPipelines}
                            <button
                                class="tertiary"
                                on:click={() => {
                                    hideOldPipelines = false;
                                    showPipelines($allPipelines);
                                }}
                                use:tooltip={{content: "List pipelines that have not yet finished", placement: "right", delay: [1000, 0]}}>
                                Show older
                            </button>
                        {:else}
                            <button
                                class="tertiary"
                                on:click={() => {
                                    hideOldPipelines = true;
                                    showPipelines($allPipelines);
                                }}
                                use:tooltip={{content: "Only show pipelines newer than three days", placement: "right", delay: [1000, 0]}}>
                                Hide older
                            </button>
                        {/if}
                    </td>
                </tr>
            </tfoot>
        </table>
    </pipelines-pane>
    <session-pane>
        <h2 style="display:inline-block">Scanning sessions</h2>
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <refresh-button class={$isSessionsListLoading ? "rotate" : ""} on:click={onRefreshSessions}>
            <Refresh color="currentColor" size="1.5em" />
        </refresh-button>
        {#if $isSessionsListLegacy}<annotation>Legacy sessions API</annotation>
        {:else if isNumeric($sessionsPageSize) && isNumeric($sessionsSkip)}
            {#if !$isSessionsListLoading}
                <annotation>
                    {#if $availableSessionsTotalCount > 0}
                        Showing {$sessionsSkip + 1}..{$sessionsSkip + 1 + $availableSessions?.length - 1} out of available total
                        {$availableSessionsTotalCount}
                    {/if}
                </annotation>
            {:else}
                <annotation>Loading...</annotation>
            {/if}
        {/if}
        <table>
            <thead>
                <th style="cursor:pointer;" on:click={() => $sessionFilter.sortOrder === sortOrder[2]._id ? onClickOrder(sortOrder[3]._id) : onClickOrder(sortOrder[2]._id)}>
                    <text-with-icon>
                        Scan time
                        <span >
                            {#if $sessionFilter.sortOrder === sortOrder[2]._id}
                                <ChevronUp color={sortOrder[2]._id ?"#0080e1" :""} size={"1.5rem"}/>
                                {:else}
                                    <ChevronUp/>
                            {/if}
                            {#if $sessionFilter.sortOrder === sortOrder[3]._id}
                                <Chevrondown color={sortOrder[3]._id ?"#0080e1" :""} size={"1.5rem"}/>
                                {:else}
                                    <Chevrondown/>
                            {/if}
                        </span>
                    </text-with-icon>
                </th>
                <th style="cursor:pointer;" on:click={() => $sessionFilter.sortOrder === sortOrder[0]._id ? onClickOrder(sortOrder[1]._id) : onClickOrder(sortOrder[0]._id)}>
                    <text-with-icon>
                        Name
                        <span >
                            {#if $sessionFilter.sortOrder === sortOrder[0]._id}
                                <ChevronUp color={sortOrder[0]._id ?"#0080e1" :""} size={"1.5rem"}/>
                                {:else}
                                    <ChevronUp/>
                            {/if}
                            {#if $sessionFilter.sortOrder === sortOrder[1]._id}
                                <Chevrondown color={sortOrder[1]._id ?"#0080e1" :""} size={"1.5rem"}/>
                                {:else}
                                    <Chevrondown/>
                            {/if}
                        </span>
                    </text-with-icon>
                </th>
                <th>Scans</th>
                <th>Reconstructions</th>
                {#if $showDebug}
                    <th use:tooltip={{content: 'Number of deleted scans (s) or reconstructions (r)', placement: "top"}}>Deleteds</th>
                {/if}
                <th on:click|stopPropagation={openUserMenu} style="overflow: visible; cursor:pointer;">
                    <text-with-icon>
                        User
                        <span>
                            {#if $sessionFilter.userIds.length > 0}
                            <FilterMenu />
                            {:else}
                            <FilterMenuOutline />
                            {/if}
                        </span>
                        {#if openUsers}
                            <header-module>
                                {#each allMembers as member}
                                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                                    <!-- svelte-ignore a11y-no-static-element-interactions-->
                                    <header-item
                                        class:active={$sessionFilter.userIds.includes(member._id)}
                                        on:click|stopPropagation={(e) => onClickMember(e, member._id)}>
                                        <span>{member.name}</span>
                                        <div >
                                            {#if $sessionFilter.userIds.includes(member._id)}
                                            <CheckboxIntermediate />
                                            {/if}
                                            {#if !$sessionFilter.userIds.includes(member._id)}
                                            <CheckboxBlankOutline />
                                            {/if}
                                        </div>
                                    </header-item>
                                {/each}
                            </header-module>
                        {/if}
                    </text-with-icon>
                </th>
                <th on:click|stopPropagation={openTeamMenu} style="overflow: visible; cursor:pointer">
                    <text-with-icon>
                        Team
                            <span>
                                {#if $sessionFilter.teamIds.length > 0}
                                <FilterMenu />
                                {:else}
                                <FilterMenuOutline />
                                {/if}
                            </span>
                            {#if openTeams}
                                <header-module>
                                    {#each allMyTeams as team}
                                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                                        <!-- svelte-ignore a11y-no-static-element-interactions-->
                                        <header-item
                                            class:active={$sessionFilter.teamIds.includes(team._id)}
                                            on:click|stopPropagation={(e) => onClickTeam(e, team._id)}>
                                            <span>{team.name}</span>
                                            <div>
                                                {#if $sessionFilter.teamIds.includes(team._id)}
                                                <CheckboxIntermediate />
                                                {/if}
                                                {#if !$sessionFilter.teamIds.includes(team._id)}
                                                <CheckboxBlankOutline />
                                                {/if}
                                            </div>
                                        </header-item>
                                    {/each}
                                </header-module>
                            {/if}
                    </text-with-icon>
                </th>
                {#if $showDebug}
                    <th>User ID</th>
                {/if}
                <th use:tooltip={{content: "Total dataset storage size in GB.<br/> Scans and reconstructions", html: true, placement: "top", delay: [1000, 0]}}>Size</th>
            </thead>
            <tbody>
                {#if $availableSessions?.length > 0}
                    {#each $availableSessions as s}
                        <tr>
                            <td>
                                {isoDateToDate(s.start_time)}
                                {isoDateToTime(s.start_time)}
                            </td>
                            <td><a href="/#/details/{s.session_id}" on:click|preventDefault={() => onSelectSession(s)} use:tooltip={{content: s.session_id, placement: "top-right"}}>{s.name}</a></td>
                            <td>{s.no_tomographies || "0"}</td>
                            <td>{s.no_reconstructions || "0"}</td>
                            {#if $showDebug}
                                <td>
                                    {#if s.no_deleted_tomographies > 0 || s.no_deleted_reconstructions > 0}
                                        {s.no_deleted_tomographies > 0 ? s.no_deleted_tomographies + 's' : ''} {s.no_deleted_reconstructions ? s.no_deleted_reconstructions + 'r' : ''}
                                    {:else}
                                        &nbsp;
                                    {/if}
                                </td>
                            {/if}
                            <td>{s.user}</td>
                            <td>{s.team_name || ""}</td>
                            {#if $showDebug}
                                <td>{getUserFromSession(s) || ""}</td>
                            {/if}
                            <td>{s.size_GB ? Math.round(s.size_GB) + " GB" : ""}</td>
                        </tr>
                    {/each}
                {:else}
                    <tr><td colspan="100%">No sessions</td></tr>
                {/if}
            </tbody>
            <tfoot style="text-align: center; border: none;">
                <tr>
                    <td colspan="100%" style="border: none;">
                        <annotation>&nbsp;</annotation>
                        {#if $isSessionsListLegacy}
                            Legacy sessions API -- no paging
                        {/if}
                        {#if !$isSessionsListLegacy}
                            {#if isNumeric($sessionsPageSize) && isNumeric($sessionsSkip)}
                                {#if $sessionsSkip - $sessionsPageSize >= 0}
                                    <a href="." on:click|preventDefault={previousSessionPage}>Previous</a>&nbsp;&nbsp;&nbsp;
                                {/if}
                                {#if $availableSessionsTotalCount > $sessionsSkip + $sessionsPageSize}
                                    <a href="." on:click|preventDefault={nextSessionPage}>Next</a>
                                {/if}
                            {/if}
                        {/if}
                    </td>
                </tr>
            </tfoot>
        </table>
    </session-pane>
    <annotation>NOTE! All times in UTC!</annotation>
    {#if $showDebug}
        <button type="button" on:click={ () => {throw new Error("Sentry Frontend Error")} }>
            Throw error
        </button>
        <button type="button" class="red-button" on:click={ () => {throw new Error("Another uncaught Frontend Error")} }>
            Throw another error
        </button>
    {/if}

    {#if $accessInfo?.userservice_org_teams?.some(o => o.role == "admin")}
        <StorageWidget />
    {/if}
</main-window>

{#if $showDebug && $showDebugPane}
    <debug-pane>
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <close-x-button on:click={closeDebugPane}>
            <Close color="currentColor" size="1.5em" />
        </close-x-button>
        User: {$username}
        <br />
        User id: {$accessInfo?.userservice_userid}
        <br />
        User's teams: {allMyTeams?.map(t => {return `${t.name}-${t._id}`})}
        <br />
        selected teamIds: {$sessionFilter.teamIds}
        <br />
        selected userIds: {$sessionFilter.userIds}
        <br />
        X-ray machine: {machine}
        <br />
        Stack: {$stack}
        <br />
        Number of active pipelines: {activePipelines?.length}
        <br />
        Authenticated: {$isAuthenticated}
        <br />
        User location: {$userLocation}
        <br />
        Environment: {$environment}
    </debug-pane>
{/if}

<style>
    :global(body) {
        padding: 0;
        margin: 0 auto;
    }

    main-window {
        text-align: left;
        padding: 0;
        margin: 0 auto;
        display: block;
    }
    session-pane {
        width: 100%;
        height: 100%;
        display: block;
        min-height: 300px;
        /*text-align: left;*/
        padding: 0 8px 2px 8px;
        white-space: nowrap;
        overflow-x: scroll;
        overflow-y: scroll;
    }

    pipelines-pane {
        width: 100%;
        height: 100%;
        /*max-height: 20em;*/
        /*min-height: 300px;*/
        display: block;
        padding: 0 8px 2px 8px;
        overflow-x: hidden;
        /*overflow-y: scroll;*/
        background-color: var(--var-theme-base-background-color);
        color: var(--var-theme-base-color);
    }

    /** END Application-global elements */
    debug-pane {
        display: block;
        padding: 8px;
        background-color: #fafafaf0;
        font-size: small;
        text-align: left;
        overflow-wrap: break-word;
        width: 100%;
        overflow-y: scroll;
        position: fixed;
        bottom: 0;
    }

    close-x-button {
        position: absolute;
        top: 0;
        right: 32px;
        background-color: transparent;
        color: #333;
    }
    close-x-button:hover {
        cursor: pointer;
    }
    header-module {
        min-width: 250px;
        max-height: 185px;
        overflow: auto;
        position: absolute;
        display: flex;
        background-color: var(--var-theme-base-color);
        top: 100%;
        left: 0px;
        border: 1px solid var(--var-theme-base-color);
        flex-direction: column;
        z-index: 1;
        border-radius: 6px;
    }
    header-item {
        color: white;
        display: flex;
        justify-content: space-between;
        padding: 3px 10px;
        cursor: pointer;
        flex-shrink: 0;
        max-width: 290px;
    }

    header-item > span {
        margin-right: 10px;
        text-overflow: ellipsis;
        overflow: hidden;
    }

    header-item:hover {
        background-color: #3c3c3c;
    }
    .active {
        background-color: #575656;
    }

    th {
        position: relative;
        overflow: auto;
    }
    text-with-icon {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    text-with-icon > span {
        display: flex;
        flex-direction: column;
        margin-left: 1rem;
        align-items: center;
        height: 2em;
        justify-content: center;
    }

    .red-button {
        background-color:red;
    }
    .red-button:hover {
        background-color:#0080e1;
    }

    .red-button:not(:disabled):active {
        background-color: #ddd;
    }
</style>
